// src/components/AttendanceExport.jsx
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  CircularProgress,
  Fade,
  Grow
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { jwtDecode } from 'jwt-decode';
import pdfMake from "pdfmake/build/pdfmake.min";
import pdfFonts from "pdfmake/build/vfs_fonts";

// Icons
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import FilterAltIcon from '@mui/icons-material/FilterAlt';

if (pdfFonts && pdfFonts.pdfMake) {
  pdfMake.vfs = pdfFonts.pdfMake.vfs;
}

const INVALID_VALUES = new Set([
  'UNKNOWN', 'WFFWFW', 'ASWW', 'TEST', 'ALL', 'UNDEFINED', 'NULL', ''
]);

// Helper to convert /logo.png to base64 for PDF rendering & Excel insertion
const fetchLogoBase64 = async () => {
  try {
    const response = await fetch('/logo.png');
    if (!response.ok) return null;
    const blob = await response.blob();
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
};

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`attendance-export-tabpanel-${index}`}
    aria-labelledby={`attendance-export-tab-${index}`}
    {...other}
  >
    {value === index && (
      <Grow in={value === index} timeout={400}>
        <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
          {children}
        </Box>
      </Grow>
    )}
  </div>
);

const AttendanceExport = () => {
  const theme = useTheme();
  const [value, setValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [period, setPeriod] = useState(new Date().toISOString().slice(0, 7)); // Default YYYY-MM
  const [logoBase64, setLogoBase64] = useState(null);

  useEffect(() => {
    fetchLogoBase64().then((b64) => {
      if (b64) setLogoBase64(b64);
    });
  }, []);

  // JWT Token Decode & Role Normalization
  const token = localStorage.getItem('token');
  const decoded = useMemo(() => {
    if (!token) return null;
    try {
      return jwtDecode(token);
    } catch {
      return null;
    }
  }, [token]);

  const normalizedRole = useMemo(() => {
    const r = String(decoded?.role || decoded?.userRole || '').trim().toLowerCase();
    if (r === 'poweruser' || r === 'power user' || decoded?.isPowerUser) return 'powerUser';
    if (r === 'admin') return 'admin';
    return 'user';
  }, [decoded]);

  const userName = decoded?.name || decoded?.userName || decoded?.empName || 'Shivam Kumar';
  const userEmpCode = decoded?.empCode || decoded?.id || '115061';
  const roleDisplay = normalizedRole === 'powerUser' ? 'Power User' : normalizedRole === 'admin' ? 'Project Admin' : 'User';

  const [filters, setFilters] = useState({
    projectCode: '',
    divCode: 'KOL',
    status: '',
    reviewStatus: '',
    category: '',
    subContractor: '',
    dateFrom: '',
    dateTo: ''
  });

  const [filterOptions, setFilterOptions] = useState({
    projectCodes: [],
    divCodes: [],
    statuses: [],
    reviewStatuses: [],
    categories: [],
    subContractors: []
  });

  // Apply Role Scoping
  useEffect(() => {
    if (normalizedRole === 'admin' && decoded?.projectCode) {
      setFilters((prev) => ({ ...prev, projectCode: decoded.projectCode }));
    }
  }, [normalizedRole, decoded]);

  // Fetch Filter Dropdowns
  useEffect(() => {
    axios.get('https://gannondunkerley.in/reports/filters')
      .then((res) => {
        setFilterOptions(res.data || {});
      })
      .catch((err) => {
        console.error('Error fetching filter options:', err);
      });
  }, []);

  // Clean Project Codes
  const cleanProjectCodes = useMemo(() => {
    const list = filterOptions.projectCodes || [];
    const unique = new Set();
    list.forEach((c) => {
      const val = String(c || '').trim().toUpperCase();
      if (val && !INVALID_VALUES.has(val)) unique.add(val);
    });
    return Array.from(unique).sort();
  }, [filterOptions.projectCodes]);

  // Clean Division Codes
  const cleanDivCodes = useMemo(() => {
    const list = filterOptions.divCodes || [];
    const unique = new Set();
    list.forEach((d) => {
      const val = String(d || '').trim().toUpperCase();
      if (val && !INVALID_VALUES.has(val)) unique.add(val);
    });
    return Array.from(unique).sort();
  }, [filterOptions.divCodes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'category' && value.toLowerCase() === 'employee') {
      setFilters((prev) => ({ ...prev, category: value, subContractor: '' }));
    } else {
      setFilters((prev) => ({ ...prev, [name]: value }));
    }
  };

  const isEmployeeCategory = filters.category?.toLowerCase() === 'employee';

  const getFormattedTimestamp = () => {
    return new Date().toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    });
  };

  // Helper to load ExcelJS dynamically for Image Embedding in Excel
  const loadExcelJS = async () => {
    if (window.ExcelJS) return window.ExcelJS;
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.3.0/exceljs.min.js';
      script.onload = () => resolve(window.ExcelJS);
      script.onerror = reject;
      document.body.appendChild(script);
    });
  };

  // 1. Daily Raw Attendance Export
  const handleExport = async () => {
    if (!filters.dateFrom || !filters.dateTo) {
      alert('Please select both From Date and To Date');
      return;
    }

    setLoading(true);
    try {
      const exportParams = { ...filters };
      if (exportParams.category?.toLowerCase() === 'employee') {
        delete exportParams.subContractor;
      }

      const query = new URLSearchParams(exportParams).toString();
      const response = await axios.get(`https://gannondunkerley.in/reports/attendance?${query}`);
      const rawData = Array.isArray(response.data) ? response.data : response.data?.data || [];

      if (!rawData.length) {
        alert('No attendance records found for the selected date range.');
        setLoading(false);
        return;
      }

      const generatedOn = getFormattedTimestamp();

      // ExcelJS for Logo embed in Excel
      try {
        const ExcelJS = await loadExcelJS();
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Daily Attendance Logs');

        // Add Image
        if (logoBase64) {
          const imageId = workbook.addImage({
            base64: logoBase64,
            extension: 'png'
          });
          worksheet.addImage(imageId, {
            tl: { col: 0.2, row: 0.2 },
            ext: { width: 48, height: 48 }
          });
        }

        // Header Rows
        worksheet.addRow([]);
        worksheet.addRow(['', 'GANNON DUNKERLEY & CO., LTD.']);
        worksheet.addRow(['', 'Kolkata Division — Centralized Biometric Data Summary Report']);
        worksheet.addRow(['', `Downloaded By: ${userName} (#${userEmpCode}) [${roleDisplay}]  |  Downloaded / Live Time: ${generatedOn}`]);
        worksheet.addRow(['', `Period: ${filters.dateFrom} to ${filters.dateTo}  |  Project: ${filters.projectCode || 'ALL'}  |  Division: ${filters.divCode || 'KOL'}`]);
        worksheet.addRow([]);

        // Table Header
        const headerRow = worksheet.addRow([
          'SL No',
          'Emp Code',
          'Employee Name',
          'Project Code',
          'Division',
          'Punch Status',
          'Review Status',
          'Category',
          ...(isEmployeeCategory ? [] : ['Sub Contractor']),
          'Punch Timestamp'
        ]);

        headerRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0284C7' } };
        });

        rawData.forEach((row, idx) => {
          worksheet.addRow([
            idx + 1,
            row.empCode || '',
            row.name || 'Staff Member',
            row.projectCode || '',
            row.divCode || '',
            row.status || '',
            row.reviewStatus || '',
            row.category || '',
            ...(isEmployeeCategory ? [] : [row.subContractor || '—']),
            row.timestamp ? new Date(row.timestamp).toLocaleString('en-IN') : ''
          ]);
        });

        worksheet.columns = [
          { width: 8 },
          { width: 14 },
          { width: 28 },
          { width: 14 },
          { width: 12 },
          { width: 14 },
          { width: 14 },
          { width: 14 },
          ...(isEmployeeCategory ? [] : [{ width: 20 }]),
          { width: 24 }
        ];

        const buffer = await workbook.xlsx.writeBuffer();
        const dataBlob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        saveAs(dataBlob, `GDCL_Daily_Attendance_${filters.dateFrom}_to_${filters.dateTo}.xlsx`);

      } catch (excelErr) {
        // Fallback to SheetJS if external script fails
        const sheetAOA = [
          ['GANNON DUNKERLEY & CO., LTD.'],
          ['Kolkata Division — Centralized Biometric Data Summary Report'],
          [`Downloaded By: ${userName} (#${userEmpCode}) [${roleDisplay}]  |  Downloaded / Live Time: ${generatedOn}`],
          [`Period: ${filters.dateFrom} to ${filters.dateTo}  |  Project: ${filters.projectCode || 'ALL'}  |  Division: ${filters.divCode || 'KOL'}`],
          [],
          [
            'SL No', 'Emp Code', 'Employee Name', 'Project Code', 'Division',
            'Punch Status', 'Review Status', 'Category',
            ...(isEmployeeCategory ? [] : ['Sub Contractor']),
            'Punch Timestamp'
          ]
        ];

        rawData.forEach((row, idx) => {
          sheetAOA.push([
            idx + 1,
            row.empCode || '',
            row.name || 'Staff Member',
            row.projectCode || '',
            row.divCode || '',
            row.status || '',
            row.reviewStatus || '',
            row.category || '',
            ...(isEmployeeCategory ? [] : [row.subContractor || '—']),
            row.timestamp ? new Date(row.timestamp).toLocaleString('en-IN') : ''
          ]);
        });

        const worksheet = XLSX.utils.aoa_to_sheet(sheetAOA);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Daily Attendance Logs');
        const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
        saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), `GDCL_Daily_Attendance_${filters.dateFrom}_to_${filters.dateTo}.xlsx`);
      }

    } catch (err) {
      console.error('Export failed:', err);
      alert('Export failed. Please check network/parameters.');
    } finally {
      setLoading(false);
    }
  };

  // 2. Monthly Report Summary Export (Excel & PDF)
  const exportSummary = async (type) => {
    if (!period) {
      alert('Please select a period (Month & Year)');
      return;
    }

    setLoading(true);
    try {
      const summaryParams = { ...filters, period };
      if (summaryParams.category?.toLowerCase() === 'employee') {
        delete summaryParams.subContractor;
      }

      const query = new URLSearchParams(summaryParams).toString();
      const response = await fetch(`https://gannondunkerley.in/attendance/summary?${query}`);
      const summaryData = await response.json();

      const employeeResponse = await fetch(`https://gannondunkerley.in/employees`);
      const employeeJson = await employeeResponse.json();
      const employeeData = employeeJson.EmployeeList || [];

      const empMap = {};
      employeeData.forEach((emp) => {
        empMap[emp.empCode] = {
          name: emp.name || '',
          divCode: emp.divCode || '',
          projectCode: emp.projectCode || '',
          category: emp.category || ''
        };
      });

      const [year, month] = period.split('-').map(Number);
      const daysInMonth = new Date(year, month, 0).getDate();
      const generatedOn = getFormattedTimestamp();

      if (type === "excel") {
        try {
          const ExcelJS = await loadExcelJS();
          const workbook = new ExcelJS.Workbook();
          const worksheet = workbook.addWorksheet('Monthly Report Summary');

          if (logoBase64) {
            const imageId = workbook.addImage({
              base64: logoBase64,
              extension: 'png'
            });
            worksheet.addImage(imageId, {
              tl: { col: 0.2, row: 0.2 },
              ext: { width: 48, height: 48 }
            });
          }

          worksheet.addRow([]);
          worksheet.addRow(['', 'GANNON DUNKERLEY & CO., LTD.']);
          worksheet.addRow(['', 'Kolkata Division — Centralized Biometric Data Summary Report']);
          worksheet.addRow(['', `Downloaded By: ${userName} (#${userEmpCode}) [${roleDisplay}]  |  Downloaded / Live Time: ${generatedOn}`]);
          worksheet.addRow(['', `Period: ${period}  |  Project: ${filters.projectCode || 'ALL'}  |  Division: ${filters.divCode || 'KOL'}`]);
          worksheet.addRow([]);

          const tableHeaders = ['Div', 'P_Code', 'Category', 'EmpCode', 'Name'];
          for (let i = 1; i <= daysInMonth; i++) {
            tableHeaders.push(`Day ${i}`);
          }
          tableHeaders.push('W.Days', 'P', 'A', 'L');

          const headerRow = worksheet.addRow(tableHeaders);
          headerRow.eachCell((cell) => {
            cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0284C7' } };
          });

          summaryData.forEach((emp) => {
            const empInfo = empMap[emp.empCode] || {};
            const row = [
              empInfo.divCode || '',
              empInfo.projectCode || '',
              empInfo.category || '',
              emp.empCode || '',
              empInfo.name || ''
            ];

            for (let i = 1; i <= daysInMonth; i++) {
              const statusObj = emp.summary?.find((s) => s.day === i);
              row.push(statusObj?.status || 'A');
            }

            row.push(daysInMonth);
            row.push((emp.P || 0) + (emp.L || 0) + (emp.OD || 0) + (emp.H || 0) + (emp.WO || 0));
            row.push(emp.A || 0);
            row.push(emp.L || 0);

            worksheet.addRow(row);
          });

          const buffer = await workbook.xlsx.writeBuffer();
          const dataBlob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
          saveAs(dataBlob, `GDCL_Monthly_Summary_Report_${period}.xlsx`);

        } catch (excelErr) {
          // Fallback if script fails
          const headerRows = [
            ['GANNON DUNKERLEY & CO., LTD.'],
            ['Kolkata Division — Centralized Biometric Data Summary Report'],
            [`Downloaded By: ${userName} (#${userEmpCode}) [${roleDisplay}]  |  Downloaded / Live Time: ${generatedOn}`],
            [`Period: ${period}  |  Project: ${filters.projectCode || 'ALL'}  |  Division: ${filters.divCode || 'KOL'}`],
            []
          ];

          const tableHeaders = ['Div', 'P_Code', 'Category', 'EmpCode', 'Name'];
          for (let i = 1; i <= daysInMonth; i++) {
            tableHeaders.push(`Day ${i}`);
          }
          tableHeaders.push('W.Days', 'P', 'A', 'L');
          headerRows.push(tableHeaders);

          summaryData.forEach((emp) => {
            const empInfo = empMap[emp.empCode] || {};
            const row = [
              empInfo.divCode || '',
              empInfo.projectCode || '',
              empInfo.category || '',
              emp.empCode || '',
              empInfo.name || ''
            ];

            for (let i = 1; i <= daysInMonth; i++) {
              const statusObj = emp.summary?.find((s) => s.day === i);
              row.push(statusObj?.status || 'A');
            }

            row.push(daysInMonth);
            row.push((emp.P || 0) + (emp.L || 0) + (emp.OD || 0) + (emp.H || 0) + (emp.WO || 0));
            row.push(emp.A || 0);
            row.push(emp.L || 0);

            headerRows.push(row);
          });

          const worksheet = XLSX.utils.aoa_to_sheet(headerRows);
          const workbook = XLSX.utils.book_new();
          XLSX.utils.book_append_sheet(workbook, worksheet, "Monthly Report Summary");
          const excelBuffer = XLSX.write(workbook, { bookType: 'xlsx', type: 'array' });
          saveAs(new Blob([excelBuffer], { type: 'application/octet-stream' }), `GDCL_Monthly_Summary_Report_${period}.xlsx`);
        }

      } else if (type === "pdf") {
        // Headers with W.Days, P, A, L
        const headers = [
          "Div", "Project", "Category", "EmpCode", "Name",
          ...Array.from({ length: daysInMonth }, (_, i) => `D${i + 1}`),
          "W.Days", "P", "A", "L"
        ];

        const body = [headers];
        summaryData.forEach((emp) => {
          const empInfo = empMap[emp.empCode] || {};
          const rowArr = [
            empInfo.divCode || "",
            empInfo.projectCode || "",
            empInfo.category || "",
            emp.empCode || "",
            empInfo.name || "",
          ];

          for (let i = 1; i <= daysInMonth; i++) {
            const statusObj = emp.summary?.find((s) => s.day === i);
            rowArr.push(statusObj?.status || "A");
          }

          rowArr.push(
            daysInMonth,
            (emp.P || 0) + (emp.L || 0) + (emp.OD || 0) + (emp.H || 0) + (emp.WO || 0),
            emp.A || 0,
            emp.L || 0
          );

          body.push(rowArr);
        });

        const pdfContent = [];
        if (logoBase64) {
          pdfContent.push({
            image: logoBase64,
            width: 44,
            alignment: 'left',
            margin: [0, 0, 0, 4]
          });
        }

        pdfContent.push(
          { text: "GANNON DUNKERLEY & CO., LTD.", style: "mainHeader" },
          { text: "Kolkata Division — Centralized Biometric Data Summary Report", style: "subHeader" },
          { 
            text: `Downloaded By: ${userName} (#${userEmpCode}) [${roleDisplay}]  |  Downloaded / Live Time: ${generatedOn}  |  Period: ${period}`, 
            style: "metaText" 
          },
          {
            table: {
              headerRows: 1,
              dontBreakRows: true,
              // EXACT PROPORTIONS TO PREVENT OVERFLOW & CROPPING:
              // 5 info columns + N days columns + 4 summary totals (W.Days, P, A, L)
              widths: [
                16, // Div
                24, // Project
                28, // Category
                26, // EmpCode
                58, // Name
                ...Array(daysInMonth).fill('*'), // Days evenly stretch
                18, // W.Days (FULLY VISIBLE)
                14, // P (Present)
                14, // A (Absent)
                14  // L (Leave)
              ],
              body
            },
            layout: {
              hLineWidth: () => 0.5,
              vLineWidth: () => 0.5,
              hLineColor: () => '#cbd5e1',
              vLineColor: () => '#cbd5e1',
              paddingLeft: () => 2,
              paddingRight: () => 2,
              paddingTop: () => 2.5,
              paddingBottom: () => 2.5
            }
          }
        );

        const docDefinition = {
          pageSize: "A3",
          pageOrientation: "landscape",
          pageMargins: [15, 15, 15, 15],
          defaultStyle: { fontSize: 6 },
          content: pdfContent,
          styles: {
            mainHeader: { fontSize: 13, bold: true, color: '#0f172a', margin: [0, 0, 0, 2] },
            subHeader: { fontSize: 9.5, bold: true, color: '#0284c7', margin: [0, 0, 0, 3] },
            metaText: { fontSize: 7, color: '#475569', margin: [0, 0, 0, 8] }
          }
        };

        pdfMake.createPdf(docDefinition).download(`GDCL_Monthly_Summary_Report_${period}.pdf`);
      }
    } catch (err) {
      console.error("Summary export error:", err);
      alert("Error generating summary report.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box className="content" sx={{ p: { xs: 2, md: 3.5 }, bgcolor: '#f4f6fa', minHeight: '100vh' }}>
      
      {/* Top Banner with Clean Animation */}
      <Fade in timeout={500}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a', letterSpacing: '-0.5px' }}>
            Attendance Export Portal
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748b', mt: 0.5 }}>
            Generate and download verified biometric daily attendance audit spreadsheets.
          </Typography>
        </Box>
      </Fade>

      {/* Main Container */}
      <Paper elevation={0} sx={{
        borderRadius: 3,
        bgcolor: '#ffffff',
        border: '1px solid #e2e8f0',
        overflow: 'hidden',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.04)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': { boxShadow: '0 12px 32px rgba(15, 23, 42, 0.06)' }
      }}>
        <Tabs
          value={value}
          onChange={(e, val) => setValue(val)}
          indicatorColor="primary"
          textColor="primary"
          sx={{
            borderBottom: '1px solid #e2e8f0',
            bgcolor: '#f8fafc',
            '& .MuiTab-root': {
              fontWeight: 700,
              textTransform: 'none',
              fontSize: '0.95rem',
              py: 2,
              transition: 'color 0.2s ease',
              '&.Mui-selected': { color: '#0284c7' }
            }
          }}
        >
          <Tab label="1. Daily Attendance Punch Logs (Excel)" />
          <Tab label="2. Monthly Report Summary (Excel & PDF)" />
        </Tabs>

        {/* Filters Section */}
        <Box sx={{ p: 3, borderBottom: '1px solid #f1f5f9' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
            <FilterAltIcon sx={{ color: '#0284c7', fontSize: 20 }} />
            <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#0f172a' }}>
              Export Filter Constraints
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
            {/* Project Code */}
            {normalizedRole === 'admin' ? (
              <TextField
                size="small"
                label="Project Code"
                value={decoded?.projectCode || ''}
                disabled
                sx={{ minWidth: 180, flex: '1 1 180px', bgcolor: '#f8fafc' }}
              />
            ) : (
              <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 180px' }}>
                <InputLabel>Project Code</InputLabel>
                <Select
                  name="projectCode"
                  value={filters.projectCode}
                  label="Project Code"
                  onChange={handleChange}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Projects</MenuItem>
                  {cleanProjectCodes.map((c) => (
                    <MenuItem key={c} value={c}>{c}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {/* Division */}
            <FormControl size="small" sx={{ minWidth: 160, flex: '1 1 160px' }}>
              <InputLabel>Division</InputLabel>
              <Select
                name="divCode"
                value={filters.divCode}
                label="Division"
                onChange={handleChange}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Divisions</MenuItem>
                {cleanDivCodes.map((d) => (
                  <MenuItem key={d} value={d}>{d}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Status */}
            <FormControl size="small" sx={{ minWidth: 160, flex: '1 1 160px' }}>
              <InputLabel>Status</InputLabel>
              <Select
                name="status"
                value={filters.status}
                label="Status"
                onChange={handleChange}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Statuses</MenuItem>
                {(filterOptions.statuses || []).map((s) => (
                  <MenuItem key={s} value={s}>{s}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Review Status */}
            <FormControl size="small" sx={{ minWidth: 160, flex: '1 1 160px' }}>
              <InputLabel>Review Status</InputLabel>
              <Select
                name="reviewStatus"
                value={filters.reviewStatus}
                label="Review Status"
                onChange={handleChange}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All</MenuItem>
                {(filterOptions.reviewStatuses || []).map((r) => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Category */}
            <FormControl size="small" sx={{ minWidth: 160, flex: '1 1 160px' }}>
              <InputLabel>Category</InputLabel>
              <Select
                name="category"
                value={filters.category}
                label="Category"
                onChange={handleChange}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">All Categories</MenuItem>
                {(filterOptions.categories || []).map((cat) => (
                  <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Sub Contractor */}
            {!isEmployeeCategory && (
              <FormControl size="small" sx={{ minWidth: 180, flex: '1 1 180px' }}>
                <InputLabel>Sub Contractor</InputLabel>
                <Select
                  name="subContractor"
                  value={filters.subContractor}
                  label="Sub Contractor"
                  onChange={handleChange}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">All Sub Contractors</MenuItem>
                  {(filterOptions.subContractors || []).map((sc) => (
                    <MenuItem key={sc} value={sc}>{sc}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        </Box>

        {/* TAB 1: DAILY EXCEL EXPORT */}
        <TabPanel value={value} index={0}>
          <Box sx={{
            p: 3,
            bgcolor: '#ffffff',
            borderRadius: 2,
            border: '1px solid #e2e8f0',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2.5
          }}>
            <TextField
              size="small"
              label="From Date"
              name="dateFrom"
              type="date"
              value={filters.dateFrom}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />

            <TextField
              size="small"
              label="To Date"
              name="dateTo"
              type="date"
              value={filters.dateTo}
              onChange={handleChange}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 200 }}
            />

            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
              onClick={handleExport}
              disabled={loading}
              sx={{
                height: 40,
                px: 3,
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
                bgcolor: '#0284c7',
                boxShadow: '0 4px 12px rgba(2, 132, 199, 0.2)',
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: '#0369a1', transform: 'translateY(-2px)' }
              }}
            >
              {loading ? 'Exporting...' : 'Export to Excel (.xlsx)'}
            </Button>
          </Box>
        </TabPanel>

        {/* TAB 2: MONTHLY REPORT SUMMARY EXPORT */}
        <TabPanel value={value} index={1}>
          <Box sx={{
            p: 3,
            bgcolor: '#ffffff',
            borderRadius: 2,
            border: '1px solid #e2e8f0',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 2.5
          }}>
            <TextField
              size="small"
              label="Select Month & Year"
              type="month"
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ minWidth: 220 }}
            />

            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <FileDownloadIcon />}
              onClick={() => exportSummary("excel")}
              disabled={loading}
              sx={{
                height: 40,
                px: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
                bgcolor: '#16a34a',
                boxShadow: '0 4px 12px rgba(22, 163, 74, 0.2)',
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: '#15803d', transform: 'translateY(-2px)' }
              }}
            >
              Export Summary Report (Excel)
            </Button>

            <Button
              variant="contained"
              startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <PictureAsPdfIcon />}
              onClick={() => exportSummary("pdf")}
              disabled={loading}
              sx={{
                height: 40,
                px: 2.5,
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 2,
                bgcolor: '#dc2626',
                boxShadow: '0 4px 12px rgba(220, 38, 38, 0.2)',
                transition: 'all 0.2s ease',
                '&:hover': { bgcolor: '#b91c1c', transform: 'translateY(-2px)' }
              }}
            >
              Export Summary Report (PDF)
            </Button>
          </Box>
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default AttendanceExport;